// src/hooks/useCardCreation.ts
import { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCard } from "./useCard";
import { usePaystack } from "./usePaystack";
import { useAuth } from "./useAuth";
import { CardCustomization, CardData } from "../types/card.types";
import { calculatePetals } from "../utils/petals.utils";
import toast from "react-hot-toast";

export const useCardCreation = () => {
  const { user } = useAuth();
  const { cardId } = useParams<{ cardId?: string }>();
  const { createCard, updateCard, card: fetchedCard, loading: cardLoading } = useCard(cardId);
  const { loading: paymentLoading } = usePaystack();
  const navigate = useNavigate();

  const [createdCard, setCreatedCard] = useState<CardData | null>(null);
  const [savedCustomization, setSavedCustomization] = useState<CardCustomization | null>(null);

  useEffect(() => {
    if (fetchedCard && fetchedCard.status === "draft" && fetchedCard.isEditable) {
      const customization: CardCustomization = {
        recipientType: fetchedCard.recipientType,
        recipientName: fetchedCard.recipientName,
        recipientImage: fetchedCard.recipientImage,
        senderName: fetchedCard.senderName,
        senderEmail: fetchedCard.senderEmail,
        title: fetchedCard.title,
        message: fetchedCard.message,
        personalNote: fetchedCard.personalNote,
        memory: fetchedCard.memory,
        insideJoke: fetchedCard.insideJoke,
        archetype: fetchedCard.archetype as any,
        theme: fetchedCard.theme,
        primaryColor: fetchedCard.primaryColor,
        secondaryColor: fetchedCard.secondaryColor,
        accentColor: fetchedCard.accentColor,
        fontFamily: fetchedCard.fontFamily,
        includeBouquet: fetchedCard.includeBouquet,
        bouquetAmount: fetchedCard.bouquetAmount,
        petals: fetchedCard.petals,
        includeHeartParticles: false,
        includeSparkles: false,
      };

      setSavedCustomization(customization);
      setCreatedCard(fetchedCard);
    }
  }, [fetchedCard]);

  const handleSave = useCallback(
    async (customization: CardCustomization): Promise<CardData | null> => {
      try {
        const cardData = {
          ...customization,
          petals: calculatePetals(customization.bouquetAmount),
          includeHeartParticles: false,
          includeSparkles: false,
          animationSpeed: "normal" as const,
          isEditable: true,
          status: "draft" as const,
        };

        let card: CardData | null = null;

        if (createdCard?.id && createdCard.status === "draft") {
          card = await updateCard(createdCard.id, {
            customization: cardData,
            is_editable: true,
            petals: cardData.petals,
          });
          toast.success("Design updated successfully!");
        } else {
          card = await createCard(cardData);
          if (card) {
            toast.success("Design saved successfully!");
          }
        }

        if (card) {
          setCreatedCard(card);

          sessionStorage.setItem(
            "lastCustomization",
            JSON.stringify({
              cardId: card.id,
              customization: cardData,
              timestamp: Date.now(),
            })
          );

          return card;
        }

        return null;
      } catch (error) {
        console.error("Error saving card:", error);
        toast.error("Failed to save design. Please try again.");
        return null;
      }
    },
    [createCard, updateCard, createdCard]
  );

  const loadDraftFromStorage = useCallback((): {
    cardId?: string;
    customization: CardCustomization;
  } | null => {
    const saved = sessionStorage.getItem("lastCustomization");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Date.now() - parsed.timestamp < 60 * 60 * 1000) {
          return parsed;
        }
      } catch (e) {}
    }
    return null;
  }, []);

  const clearDraft = useCallback(() => {
    sessionStorage.removeItem("lastCustomization");
  }, []);

  const handlePayment = useCallback(() => {
    if (!createdCard) {
      toast.error("Please save your card first");
      return;
    }

    if (!user?.email) {
      toast.error("Please sign in to continue");
      navigate("/signin");
      return;
    }

    navigate(`/payment/${createdCard.id}`);
  }, [createdCard, user, navigate]);

  return {
    createdCard,
    savedCustomization,
    cardLoading,
    paymentLoading,
    handleSave,
    handlePayment,
    loadDraftFromStorage,
    clearDraft,
  };
};
